import {
    DataTable,
    DataTablePresenter,
    ITableColumn,
    ProviderExperimentClaim,
    TitleCard,
    BaseSelect,
} from '@enter/core';
import { BaseButton } from '@enter/core';
import React, { useState, useCallback, useMemo } from 'react';
import styled from '@emotion/styled/macro';
import { format } from 'date-fns';

const StyledTitleCard = styled(TitleCard)`
    justify-content: space-between;
    max-width: 100%;
`;

const CardBody = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 100%;
`;

const StyledDataTable = styled(DataTable)`
    display: flex;
    flex-direction: column;
    margin: 16px 0;
    flex: 1 1 100%;
    box-shadow: 0 0.5px 0 0 #e5e5e5;

    [role='columnheader'] {
        padding: 8px 12px 6px 12px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    [role='cell'] {
        display: inline-block;
        padding: 8px 12px 6px 12px;
        line-height: 24px;
        font-size: 15px;
        color: #666;

        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const CaptionContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const FilterContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const FilterLabel = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: #333;
`;

interface IClaimsTableProps {
    data: ProviderExperimentClaim[];
    isLoading?: boolean;
    onGoToClaim?: (claimId: string) => void;
    onFilterChange?: (filterValue: string) => void;
}

export const ClaimsTable: React.FC<IClaimsTableProps> = ({
    data,
    isLoading = false,
    onGoToClaim,
    onFilterChange,
}) => {
    const [inExperimentSelect, setInExperimentSelect] = useState<string>('Test Claims');

    const handleFilterChange = useCallback((value: string) => {
        setInExperimentSelect(value);
        onFilterChange?.(value);
    }, [onFilterChange]);

    const columns: ITableColumn<ProviderExperimentClaim>[] = useMemo(() => [
        {
            id: 'id',
            Header: 'ID',
            accessor: (row) => row.claim?.id || row.id || 'N/A',
            Cell: ({ value }) => <span style={{ fontWeight: '500' }}>{value || 'N/A'}</span>,
            minWidth: 80,
        },
        {
            id: 'patientFirst',
            Header: 'Patient First',
            accessor: 'claim',
            Cell: ({ value }) => <span>{value?.patientFirst || 'N/A'}</span>,
            minWidth: 120,
        },
        {
            id: 'patientLast',
            Header: 'Patient Last',
            accessor: 'claim',
            Cell: ({ value }) => <span>{value?.patientLast || 'N/A'}</span>,
            minWidth: 120,
        },
        {
            id: 'fromDos',
            Header: 'From DOS',
            accessor: 'claim',
            Cell: ({ value }) => (
                <span>
                    {value?.fromDos ? format(new Date(value.fromDos), 'MM/dd/yyyy') : 'N/A'}
                </span>
            ),
            minWidth: 100,
        },
        {
            id: 'permitted',
            Header: 'Permitted',
            accessor: 'permitted',
            Cell: ({ value }) => (
                <span style={{ 
                    color: value ? '#28a745' : '#dc3545',
                    fontWeight: '500'
                }}>
                    {value ? 'Yes' : 'No'}
                </span>
            ),
            minWidth: 80,
        },
        {
            id: 'updated',
            Header: 'Updated',
            accessor: 'updated',
            Cell: ({ value }) => (
                <span>
                    {value ? format(new Date(value), 'MM/dd/yyyy HH:mm') : 'N/A'}
                </span>
            ),
            minWidth: 120,
        },
        {
            id: 'created',
            Header: 'Created',
            accessor: 'created',
            Cell: ({ value }) => (
                <span>
                    {value ? format(new Date(value), 'MM/dd/yyyy HH:mm') : 'N/A'}
                </span>
            ),
            minWidth: 120,
        },
        {
            id: 'allocatedToExperiment',
            Header: 'Is Allocated?',
            accessor: 'allocatedToExperiment',
            Cell: ({ value }) => (
                <span style={{ 
                    color: value ? '#28a745' : '#dc3545',
                    fontWeight: '500'
                }}>
                    {value ? 'Yes' : 'No'}
                </span>
            ),
            minWidth: 100,
        },
        {
            id: 'claimPassing',
            Header: 'Is Passing?',
            accessor: 'claimPassing',
            Cell: ({ value }) => (
                <span style={{ 
                    color: value ? '#28a745' : '#dc3545',
                    fontWeight: '500'
                }}>
                    {value ? 'Yes' : 'No'}
                </span>
            ),
            minWidth: 100,
        },
        {
            id: 'Action',
            Header: '',
            disableSortBy: true,
            Cell: ({ row }): React.ReactElement => {
                const claimId = row.original.claim?.id || row.original.id;
                return (
                    <div
                        onClick={(e): void => {
                            e.stopPropagation();
                        }}
                        onMouseDown={(e): void => {
                            e.stopPropagation();
                        }}
                    >
                        <BaseButton
                            variant="text"
                            size="small"
                            onClick={(e): void => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (claimId) {
                                    onGoToClaim?.(claimId);
                                }
                            }}
                        >
                            Go to Claim
                        </BaseButton>
                    </div>
                );
            },
        },
    ], [onGoToClaim]);

    const presenter = useMemo(() => new DataTablePresenter({
        name: 'claims',
        data: data,
        columns: columns,
        filterSchemas: [],
    }), [data, columns]);

    return (
        <StyledTitleCard
            size="large"
            title="Claims"
            caption={
                <CaptionContainer>
                    <FilterContainer>
                        <FilterLabel>Filter:</FilterLabel>
                        <BaseSelect
                            value={{ label: inExperimentSelect, value: inExperimentSelect }}
                            onChange={(option) => {
                                const value = Array.isArray(option) ? option[0]?.value : (option as any)?.value;
                                handleFilterChange(value || 'Test Claims');
                            }}
                            options={[
                                { label: 'Test Claims', value: 'Test Claims' },
                                { label: 'Held Claims', value: 'Held Claims' },
                            ]}
                        />
                    </FilterContainer>
                </CaptionContainer>
            }
        >
            <CardBody>
                <StyledDataTable
                    presenter={presenter}
                    isLoading={isLoading}
                    disableSorting={false}
                    hideFiltering={true}
                    hideNavigation={true}
                    hidePagination={true}
                    hideSettings={true}
                />
            </CardBody>
        </StyledTitleCard>
    );
};

